// The only place a pricing number lives, prices and plan limits alike.
// Locale bundles interpolate from here — never hardcode a literal `$digit`
// (or a limit) in a pricing.* translation key, or a change updates en and
// quietly leaves a stale number live in es/zh.
//
// There is deliberately no fourth entry: the Enterprise tier is "Let's
// talk" (OpenConceptLab/ocl_online#126). The 6-20 seat band the pricing
// study left unvalidated is not published as a price.
export const PRICES = {
  community: { monthly: 0, annual: 0 },
  pro: { monthly: 20, annual: 200 },
  team: { monthly: 79, annual: 790 },
};

export const ANNUAL_MONTHS_FREE = 2; // 2 of 12 months = 16.7% — never "Save 20%"

const usd = n => `$${n.toLocaleString('en-US')}`;

// Monthly view shows the list monthly rate; annual shows the same plan's
// effective per-month rate, so the two views are comparable at a glance.
export const monthlyPrice = key => usd(PRICES[key].monthly);
export const annualPerMonth = key => {
  const perMonth = PRICES[key].annual / 12;
  return usd(Number(perMonth.toFixed(2)));
};
export const annualTotal = key => usd(PRICES[key].annual);

// "Allowances and limits", under /pricing's availability table
// (ocl_online#448), with Jon's Oct 28 values from ocl_online#447. Community
// and Pro only: Team's wait on ocl_online#369. Each cell names its
// pricing.limits.units.* template and the numbers that fill it; null is not
// included.
export const LIMIT_PLAN_KEYS = ['community', 'pro'];
const rate = (perMinute, perDay) => ({ unit: 'rate', perMinute, perDay });
export const LIMIT_ROWS = [
  { key: 'mapper_projects', cells: { community: { unit: 'count', n: 1 }, pro: { unit: 'count', n: 5 } } },
  { key: 'mapper_rows', cells: { community: { unit: 'count', n: 25 }, pro: { unit: 'count', n: 1000 } } },
  { key: 'match_operations', cells: { community: { unit: 'oneTime', n: 100 }, pro: { unit: 'perMonth', n: 250 } } },
  { key: 'ai', cells: { community: { unit: 'callsOneTime', n: 25 }, pro: { unit: 'tokensPerMonth', n: 1000000 } } },
  // No Community preview on Nov 1, and no number for Pro (ocl_online#366, #447).
  { key: 'mcp', cells: { community: null, pro: { unit: 'included' } } },
  { key: 'api_token', cells: { community: rate(100, 5000), pro: rate(300, 10000) } },
  { key: 'apps', cells: { community: rate(500, 20000), pro: rate(500, 20000) } },
];

// Signed out, per IP address. It and Community's API limit start on Dec 1,
// when the grace period for existing accounts ends (Jon, Oct 29), and every
// API limit waits on the credential split (ocl_online#582). So the page gives
// this as the earliest date, and says today's higher limits apply until then.
export const SIGNED_OUT_RATE = rate(200, 2000);
export const REQUEST_LIMITS_START = '2026-12-01';

// A cell's numbers, written the page language's way: 1.000.000 in es.
export const limitValues = (cell, lng) => Object.fromEntries(
  Object.entries(cell).filter(([, value]) => typeof value === 'number').map(([name, value]) => [name, value.toLocaleString(lng)]));
export const limitText = (cell, t, lng) => t(`pricing.limits.units.${cell.unit}`, limitValues(cell, lng));

// A date-only string is midnight UTC; formatted in the visitor's time zone,
// it would show the day before anywhere west of Greenwich.
export const requestLimitsStart = lng => new Intl.DateTimeFormat(lng, { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(REQUEST_LIMITS_START));
